import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';

const Appointments = () => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/appointments`, {
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAppointments(data.appointments || []);
      } else if (response.status === 401) {
        navigate('/customer/login');
      } else {
        throw new Error('Failed to fetch appointments');
      }
    } catch (err) {
      setError('Failed to load appointments');
      console.error('Fetch appointments error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status, paymentStatus) => {
    // Determine display status based on appointment status and payment
    if (status === 'CANCELLED') {
      return { label: 'Cancelled', color: '#dc3545', icon: XCircle };
    }
    if (status === 'COMPLETED') {
      return { label: 'Completed', color: '#28a745', icon: CheckCircle };
    }
    if (status === 'CONFIRMED') {
      return { label: 'Confirmed', color: '#007bff', icon: CheckCircle };
    }
    if (paymentStatus === 'PAID') {
      return { label: 'Paid', color: '#17a2b8', icon: CheckCircle };
    }
    if (status === 'PENDING') {
      return { label: 'Pending', color: '#ffc107', icon: AlertCircle };
    }
    return { label: status, color: '#6c757d', icon: AlertCircle };
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-NG', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(price);
  };

  const isUpcoming = (scheduledAt) => {
    return new Date(scheduledAt) > new Date();
  };

  const filteredAppointments = appointments.filter(apt => {
    if (filter === 'all') return true;
    if (filter === 'upcoming') return isUpcoming(apt.scheduledAt) && apt.status !== 'CANCELLED';
    if (filter === 'completed') return apt.status === 'COMPLETED';
    if (filter === 'cancelled') return apt.status === 'CANCELLED';
    return true;
  });

  if (loading) {
    return (
      <div className="appointments-loading">
        <Loader2 className="spinner" size={40} />
        <p>Loading your appointments...</p>
      </div>
    );
  }

  return (
    <div className="appointments-container">
      <div className="appointments-header">
        <h1>My Appointments</h1>
        <div className="appointments-filters">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Appointments</option>
            <option value="upcoming">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="appointments-error">
          {error}
        </div>
      )}

      {filteredAppointments.length === 0 ? (
        <div className="no-appointments">
          <Calendar size={48} strokeWidth={1.5} />
          <h3>No appointments found</h3>
          <p>
            {filter === 'all'
              ? "You haven't booked any appointments yet."
              : `No ${filter} appointments.`}
          </p>
        </div>
      ) : (
        <div className="appointments-list">
          {filteredAppointments.map((appointment) => {
            const statusInfo = getStatusInfo(appointment.status, appointment.paymentStatus);
            const StatusIcon = statusInfo.icon;
            const upcoming = isUpcoming(appointment.scheduledAt);

            return (
              <div
                key={appointment.id}
                className={`appointment-card ${upcoming ? 'upcoming' : ''}`}
              >
                <div className="appointment-card-header">
                  <div className="service-info">
                    {appointment.service?.image && (
                      <img
                        src={appointment.service.image}
                        alt={appointment.service?.name}
                        className="service-image"
                      />
                    )}
                    <div className="service-details">
                      <h3>{appointment.service?.name || appointment.serviceName || 'Service'}</h3>
                      {appointment.service?.description && (
                        <p className="service-description">{appointment.service.description}</p>
                      )}
                    </div>
                  </div>
                  <div
                    className="appointment-status"
                    style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }}
                  >
                    <StatusIcon size={16} />
                    <span>{statusInfo.label}</span>
                  </div>
                </div>

                <div className="appointment-card-body">
                  <div className="appointment-detail">
                    <Calendar size={18} />
                    <div>
                      <span className="detail-label">Date</span>
                      <span className="detail-value">{formatDate(appointment.scheduledAt)}</span>
                    </div>
                  </div>

                  <div className="appointment-detail">
                    <Clock size={18} />
                    <div>
                      <span className="detail-label">Time</span>
                      <span className="detail-value">{formatTime(appointment.scheduledAt)}</span>
                    </div>
                  </div>

                  {appointment.service?.duration && (
                    <div className="appointment-detail">
                      <Clock size={18} />
                      <div>
                        <span className="detail-label">Duration</span>
                        <span className="detail-value">{appointment.service.duration} minutes</span>
                      </div>
                    </div>
                  )}

                  {appointment.store?.name && (
                    <div className="appointment-detail">
                      <MapPin size={18} />
                      <div>
                        <span className="detail-label">Location</span>
                        <span className="detail-value">{appointment.store.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="appointment-card-footer">
                  <div className="appointment-price">
                    <span className="price-label">Amount</span>
                    <span className="price-value">{formatPrice(appointment.servicePrice || appointment.service?.price || 0)}</span>
                  </div>
                  {appointment.paymentStatus === 'PAID' && (
                    <span className="paid-badge">
                      <CheckCircle size={14} />
                      Paid
                    </span>
                  )}
                </div>

                {appointment.notes && (
                  <div className="appointment-notes">
                    <strong>Notes:</strong> {appointment.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style jsx>{`
        .appointments-container {
          padding: 24px;
          max-width: 900px;
          margin: 0 auto;
        }

        .appointments-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: #64748b;
        }

        .appointments-loading .spinner {
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
          color: #0d7463;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .appointments-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .appointments-header h1 {
          font-size: 24px;
          font-weight: 700;
          color: #0f172a;
          margin: 0;
        }

        .filter-select {
          padding: 10px 16px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          cursor: pointer;
        }

        .appointments-error {
          background: #fef2f2;
          color: #dc2626;
          padding: 12px 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }

        .no-appointments {
          text-align: center;
          padding: 60px 20px;
          background: #f8fafc;
          border-radius: 16px;
          color: #64748b;
        }

        .no-appointments svg {
          color: #cbd5e1;
          margin-bottom: 16px;
        }

        .no-appointments h3 {
          font-size: 18px;
          color: #334155;
          margin: 0 0 8px 0;
        }

        .no-appointments p {
          margin: 0;
        }

        .appointments-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .appointment-card {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
          transition: all 0.2s ease;
        }

        .appointment-card.upcoming {
          border-left: 4px solid #0d7463;
        }

        .appointment-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .appointment-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .service-info {
          display: flex;
          gap: 12px;
          flex: 1;
        }

        .service-image {
          width: 60px;
          height: 60px;
          border-radius: 10px;
          object-fit: cover;
        }

        .service-details h3 {
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
          margin: 0 0 4px 0;
        }

        .service-description {
          font-size: 13px;
          color: #64748b;
          margin: 0;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .appointment-status {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .appointment-card-body {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 16px;
          padding: 16px 0;
          border-top: 1px solid #f1f5f9;
          border-bottom: 1px solid #f1f5f9;
        }

        .appointment-detail {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }

        .appointment-detail svg {
          color: #0d7463;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .detail-label {
          display: block;
          font-size: 12px;
          color: #64748b;
          margin-bottom: 2px;
        }

        .detail-value {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #0f172a;
        }

        .appointment-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 16px;
        }

        .appointment-price {
          display: flex;
          flex-direction: column;
        }

        .price-label {
          font-size: 12px;
          color: #64748b;
        }

        .price-value {
          font-size: 18px;
          font-weight: 700;
          color: #0d7463;
        }

        .paid-badge {
          display: flex;
          align-items: center;
          gap: 4px;
          background: #dcfce7;
          color: #16a34a;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
        }

        .appointment-notes {
          margin-top: 12px;
          padding: 12px;
          background: #f8fafc;
          border-radius: 8px;
          font-size: 13px;
          color: #475569;
        }

        @media (max-width: 600px) {
          .appointments-container {
            padding: 16px;
          }

          .appointments-header {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .appointment-card-body {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Appointments;
